import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, usePropertyAddressUrl, usePropertyField, useCollectionBinding, useEditMode } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { isActivePage, mergePages } from '@vibesites/runtime';
import type { PageDataMap } from '@vibesites/runtime';
// Navigation is built from the base theme's full page manifest (with
// name/path/position) merged with this repo's local src/pages.json overrides,
// using the same per-key shallow merge the runtime applies when resolving
// pages. This keeps the nav in lockstep with this theme's page renames,
// reordering, and visibility changes. The parent manifest is copied into
// src/parent-pages.json for functional parity with the base theme because
// the parent's package "exports" map doesn't expose pages.json as a subpath.
// Keep parent-pages.json in sync with @vibesites/theme-preferred/src/pages.json
// when the base theme's page manifest changes.
import parentPagesData from '../parent-pages.json';
import localPagesData from '../pages.json';
import { fillWeekHours } from './officeHours';
import { isUsableUrl, resolveScheduleTourHref } from '../utils/scheduleTourHref';

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface VirtualTourEntry {
  virtual_tour_url?: string | null;
}

const DAY_ORDER: Record<string, number> = {
  MON: 0, TUE: 1, WED: 2, THU: 3, FRI: 4, SAT: 5, SUN: 6,
};

function dayOrderIndex(day: string | null | undefined): number {
  if (!day) return 99;
  const key = day.trim().toUpperCase().slice(0, 3);
  return DAY_ORDER[key] ?? 99;
}

function formatHourTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

function formatHoursValue(openRaw: string | null | undefined, closeRaw: string | null | undefined): string {
  const open = (openRaw || '').trim();
  const close = (closeRaw || '').trim();
  const combined = `${open} ${close}`.toLowerCase();
  if (/appointment/.test(combined)) return 'By Appointment Only';
  if (!open && !close) return 'Closed';
  if (/closed/.test(combined) && !/\d/.test(combined)) return 'Closed';
  if (!open || !close) return 'Closed';
  return `${formatHourTime(open)} – ${formatHourTime(close)}`;
}

function formatDay(day: string | null | undefined): string {
  if (!day) return '';
  const upper = day.trim().toUpperCase();
  if (upper.length <= 3) return upper;
  return upper.slice(0, 3);
}

/** Plain-text for image alt — richtext resolves field tokens to HTML. */
function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface NavItem {
  label: string;
  href: string;
}

// Pages reachable through dedicated CTA buttons (utility bar / menu actions)
// or footer links rather than the primary navigation list, so they're
// excluded from the nav even when marked visible for routing.
const NAV_EXCLUDE_ALIASES = new Set([
  'home',
  'apply-now',
  'resident-access',
  'privacy-policy',
  'accessibility',
  'pet-policy',
  'schedule-a-tour',
  // Utility-bar / menu-action pages — not primary nav links
  'contact',
  'reviews',
  'sitemap',
]);

/** Desktop nav metrics — sized to match the Passage reference header
 *  (readable link labels + a substantial Schedule a Tour pill). */
const NAV_LINK_CLASS = {
  comfortable: 'px-3 xl:px-3.5 py-2 text-[13px] xl:text-sm tracking-wider',
  compact: 'px-2 py-1.5 text-xs tracking-wide',
} as const;

const NAV_CTA_CLASS = {
  comfortable: 'min-h-[44px] gap-2 px-5 xl:px-6 py-3 text-xs xl:text-sm tracking-[0.12em]',
  compact: 'min-h-[40px] gap-1.5 px-4 py-2.5 text-xs tracking-[0.1em]',
} as const;

const NAV_CLUSTER_CLASS = {
  comfortable: 'gap-1 xl:gap-1.5 ml-5 mr-3',
  compact: 'gap-0.5 ml-3 mr-2',
} as const;

/**
 * Build navigation items dynamically from the merged page manifest
 * (parent theme + this repo's local overrides). Respects the `visible` flag,
 * soft-deletes (`deleted_at`), page order (`position`), and renamed pages,
 * and excludes template pages (e.g. hyperlocal), Home (logo already links
 * home), and CTA-only utility pages.
 */
const NAV_ITEMS: NavItem[] = mergePages(
  parentPagesData as PageDataMap,
  localPagesData as PageDataMap,
)
  .filter((page) => {
    // Soft-deleted pages stay in the merge for the editor but must not appear in live nav
    if (!isActivePage(page)) return false;
    // Respect visible flag from the merged manifest (hidden/utility pages, etc.)
    if (page.visible === false) return false;
    // Exclude template pages (e.g. hyperlocal) which are generated dynamically
    if (page.template) return false;
    // Exclude pages surfaced via dedicated CTA buttons
    if (NAV_EXCLUDE_ALIASES.has(page.alias)) return false;
    return true;
  })
  .map((page) => ({
    label: page.name,
    href: page.path,
  }));

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    // No defaultField — mono_white vs mono_black is computed at runtime via luminance
    image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_mono_white_logo', 'property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'richtext', defaultValue: '<p>Community Logo</p>' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_resident_portal_url',
      suggestedFields: ['property_resident_portal_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const contactUsLinkDef: SlotGroupDefinition = {
  label: 'Contact Us Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    // No defaultField / defaultValue — href is computed at render from the
    // AnyoneHome scheduler URL (→ /schedule-a-tour) or Contact Us fallback.
    // Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const visitUsLinkDef: SlotGroupDefinition = {
  label: 'Visit Us',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time. Editor overrides still win.
    url: {
      label: 'Directions URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

// Shown only when neither a call-tracking number nor a property phone exists.
const PHONE_PLACEHOLDER = '(555) 555-5555';

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

// ============================================================================
// Menu Nav Links
// ============================================================================

function MobileNavLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate: () => void;
}) {
  return (
    <a
      href={item.href}
      onClick={onNavigate}
      className="block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_85%,transparent)] hover:text-[var(--vibe-color-text-on-secondary)] transition-colors border-b border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]"
    >
      {item.label}
    </a>
  );
}

function DesktopNavLink({
  item,
  density,
}: {
  item: NavItem;
  density: 'comfortable' | 'compact';
}) {
  return (
    <a
      href={item.href}
      className={`desktop-nav-link whitespace-nowrap font-body font-semibold uppercase transition-colors duration-300 ${NAV_LINK_CLASS[density]}`}
    >
      {item.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================

type NavLayout = 'comfortable' | 'compact' | 'drawer';

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  // null = not measured yet. CSS shows the horizontal nav at lg+ until then,
  // so wide viewports never paint hamburger-only (Passage-style extended nav).
  const [navLayout, setNavLayout] = useState<NavLayout | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const navBarRowRef = useRef<HTMLDivElement>(null);
  const logoAreaRef = useRef<HTMLDivElement>(null);
  const measureComfortableRef = useRef<HTMLDivElement>(null);
  const measureCompactRef = useRef<HTMLDivElement>(null);

  const navDensity: 'comfortable' | 'compact' =
    navLayout === 'compact' ? 'compact' : 'comfortable';

  const phoneToken = useContentToken('header.phone');
  const fallbackPhone = usePropertyField('property_phone') as string | null;
  // Resolution chain: call-tracking number (token defaultField/override) →
  // property_phone → placeholder. The token's defaultValue is empty so a missing
  // call-tracking number falls through to property_phone instead of the placeholder.
  const phone = phoneToken || fallbackPhone || PHONE_PLACEHOLDER;
  const editMode = useEditMode();
  const isEditMode = editMode?.isEditMode ?? false;

  // Heading labels for the popped-open menu sections. Plain custom tokens
  // (no defaultField) so editors can rename them without touching property
  // data; defaults match the previously-hardcoded strings.
  const visitUsLabel = useContentToken('header.visit_us_label') || 'Visit Us';
  const callUsLabel = useContentToken('header.call_us_label') || 'Call Us';
  const officeHoursLabel = useContentToken('header.office_hours_label') || 'Office Hours';

  // Visit Us is a slot group (address + directions URL + _target) so the
  // entire block is editable as a unit and the menu's "open in new tab"
  // target stays editor-controlled.
  const visitUs = useSlotGroup('header.visit_us', visitUsLinkDef);
  const fullAddress = visitUs.address || '';
  const propertyAddressUrl = usePropertyAddressUrl();

  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const logoAlt = stripHtml(logoRaw.alt || '') || 'Community Logo';

  // Read both mono variants — no defaultField in logoSlotDef so these are the
  // only source of truth when the editor hasn't set an explicit override.
  const monoWhiteLogoUrl = usePropertyField('property_mono_white_logo') as string | null;
  const monoBlackLogoUrl = usePropertyField('property_mono_black_logo') as string | null;

  // Pick the logo variant that matches the computed text color on the primary
  // background. `--vibe-color-text-on-primary` is set by the tokens generator
  // via getContrastTextColor(property_primary_color) — reading it here keeps
  // the logo flip in lockstep with the text contrast decision (no duplicated
  // luminance math, single source of truth for the threshold).
  // Defaults to mono_white (SSR-safe); updates after mount from the live CSS var.
  const [scrolledLogoVariant, setScrolledLogoVariant] = useState<'mono_white' | 'mono_black'>('mono_white');
  useEffect(() => {
    const textOnPrimary = getComputedStyle(document.documentElement)
      .getPropertyValue('--vibe-color-text-on-primary')
      .trim()
      .toLowerCase();
    // Generator emits '#1a1a1a' for dark text, '#ffffff' for light text.
    // Match dark-text shades to mono_black, otherwise mono_white.
    const isDarkText = textOnPrimary === '#1a1a1a' || textOnPrimary === '#000' || textOnPrimary === '#000000';
    setScrolledLogoVariant(isDarkText ? 'mono_black' : 'mono_white');
  }, []);

  // If the editor has set an explicit logo, use it in both states (no switching).
  // Otherwise: transparent state = always mono_white (hero bg is typically dark),
  //            scrolled state   = luminance-computed variant for primary bg.
  const customLogoUrl: string | null = logoRaw.image || null;
  const transparentLogoUrl = customLogoUrl ?? monoWhiteLogoUrl;
  const scrolledComputedLogoUrl = customLogoUrl ?? (scrolledLogoVariant === 'mono_white' ? monoWhiteLogoUrl : monoBlackLogoUrl);
  // Dual crossfade only needed when the two states would show different images.
  const useDualLogos = !customLogoUrl && !!transparentLogoUrl && !!scrolledComputedLogoUrl && transparentLogoUrl !== scrolledComputedLogoUrl;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const contactUsLink = useSlotGroup('header.contact_us_link', contactUsLinkDef);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  // Same field AHScheduler embeds — presence means the Schedule a Tour page
  // has an active scheduler; otherwise route the CTA to Contact Us.
  const anyoneHomeSchedulerUrl = usePropertyField('custom_anyonehome_scheduler_url') as string | null;
  const scheduleTourHref = resolveScheduleTourHref(scheduleTourLink.url, anyoneHomeSchedulerUrl);

  const officeHoursRaw = useCollectionBinding<OfficeHourEntry>('header.office_hours', {
    collection: 'office_hours',
  });
  // Pad to a full week so days without hours (e.g. Sat/Sun) render as "Closed".
  const officeHours = fillWeekHours(officeHoursRaw).sort((a, b) => dayOrderIndex(a.day) - dayOrderIndex(b.day));

  // Hide /virtual-tour from nav unless the property has at least one tour
  // with a usable URL (matches VirtualTourEmbed's display rule).
  const virtualTours = useCollectionBinding<VirtualTourEntry>('header.virtual_tours', {
    collection: 'virtual_tours',
  });
  const hasVirtualTours = (virtualTours || []).some((t) => isUsableUrl(t.virtual_tour_url));
  const navItems = useMemo(
    () => NAV_ITEMS.filter((item) => {
      if (item.href === '/virtual-tour' && !hasVirtualTours) return false;
      return true;
    }),
    [hasVirtualTours],
  );

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock page scroll behind the drawer — skip in the editor so the preview
  // chrome / responsive frame is not affected.
  useEffect(() => {
    if (isEditMode) return;
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen, isEditMode]);

  // Widest layout that fits beside the logo:
  // comfortable (large desktop) → compact (mid) → drawer (hamburger on tablet).
  const updateNavMode = useCallback(() => {
    const row = navBarRowRef.current;
    const logo = logoAreaRef.current;
    const measureComfortable = measureComfortableRef.current;
    const measureCompact = measureCompactRef.current;
    if (!row || !logo || !measureComfortable || !measureCompact) return;

    const LOGO_NAV_GAP = 24;
    const available = row.clientWidth - logo.offsetWidth - LOGO_NAV_GAP;
    // offsetWidth, not scrollWidth: probes are max-content and off-screen.
    const neededComfortable = measureComfortable.offsetWidth;
    const neededCompact = measureCompact.offsetWidth;
    // Probes report 0 before layout. Leave the expanded row in place until
    // there is a real measurement, so desktop does not flash a hamburger.
    if (neededComfortable < 16 || neededCompact < 16 || row.clientWidth < 16) return;

    setNavLayout((current) => {
      const openComfortable = neededComfortable + (current === 'comfortable' ? 8 : 40);
      const openCompact = neededCompact + (current === 'drawer' ? 32 : 8);

      if (openComfortable <= available) return 'comfortable';
      if (openCompact <= available) return 'compact';
      return 'drawer';
    });
  }, []);

  useLayoutEffect(() => {
    updateNavMode();
    const row = navBarRowRef.current;
    if (!row) return;

    const ro = new ResizeObserver(() => updateNavMode());
    ro.observe(row);
    if (logoAreaRef.current) ro.observe(logoAreaRef.current);

    const onResize = () => updateNavMode();
    window.addEventListener('resize', onResize);

    const onFontsReady = () => updateNavMode();
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(onFontsReady).catch(() => {});
    }

    // Logo images loading can change available width after first paint.
    const logoRoot = logoAreaRef.current;
    const images = logoRoot ? Array.from(logoRoot.querySelectorAll('img')) : [];
    images.forEach((img) => {
      if (!img.complete) img.addEventListener('load', updateNavMode);
    });

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
      images.forEach((img) => img.removeEventListener('load', updateNavMode));
    };
  }, [updateNavMode, navItems, scheduleTourLink.label]);

  // Close the drawer when horizontal nav takes over, or when the frame is
  // resized while open (responsive editor width changes).
  useEffect(() => {
    if (navLayout === 'comfortable' || navLayout === 'compact') setMenuOpen(false);
  }, [navLayout]);

  useEffect(() => {
    if (!menuOpen) return;
    const onResize = () => setMenuOpen(false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [menuOpen]);

  // URL resolution order:
  //   1. The visit_us slot group's `url` field (editor override).
  //   2. usePropertyAddressUrl() — falls through Google place URL → business URL
  //      → full address → lat/lon at the runtime layer.
  //   3. A constructed Google Maps directions URL from the address (legacy fallback).
  //   4. '#' as a last resort so the anchor stays valid.
  const directionsUrl = isUsableUrl(visitUs.url)
    ? visitUs.url!
    : (propertyAddressUrl
        || (fullAddress
            ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(String(fullAddress))}`
            : '#'));
  const directionsTarget = visitUs._target || '_blank';

  const closeMobileMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-50 w-full${navLayout === 'drawer' ? ' nav-layout-drawer' : ''}`}
        style={{ top: 'var(--offer-banner-height, 0px)', transition: 'top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
      >
        {/* Offscreen probes — must NOT live in the flex row, or flex-shrink
            collapses their width and the hamburger wins at every viewport. */}
        <div className="nav-fit-measure-wrap" aria-hidden="true">
          <div ref={measureComfortableRef} className="nav-fit-measure">
            {navItems.map((item) => (
              <span
                key={item.href}
                className={`whitespace-nowrap font-body font-semibold uppercase ${NAV_LINK_CLASS.comfortable}`}
              >
                {item.label}
              </span>
            ))}
            <span className={`inline-flex items-center font-bold uppercase whitespace-nowrap ml-4 ${NAV_CTA_CLASS.comfortable}`}>
              {scheduleTourLink.label || 'Schedule a Tour'}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </span>
          </div>
          <div ref={measureCompactRef} className="nav-fit-measure">
            {navItems.map((item) => (
              <span
                key={item.href}
                className={`whitespace-nowrap font-body font-semibold uppercase ${NAV_LINK_CLASS.compact}`}
              >
                {item.label}
              </span>
            ))}
            <span className={`inline-flex items-center font-bold uppercase whitespace-nowrap ml-3 ${NAV_CTA_CLASS.compact}`}>
              {scheduleTourLink.label || 'Schedule a Tour'}
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </span>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Utility Bar                                                      */}
        {/* ================================================================ */}
        <div className="header-utility-bar">
          <div className="px-4 sm:px-5">
            <div className="flex items-center justify-between h-9">
              {/* Left: Address + Phone (desktop) */}
              <div className="hidden md:flex items-center divide-x divide-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_20%,transparent)]">
                <EditableSlotGroup groupPath="header.visit_us" definition={visitUsLinkDef}>
                  <a
                    href={directionsUrl}
                    target={directionsTarget}
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 pr-4 text-xs tracking-wide text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{fullAddress}</span>
                  </a>
                </EditableSlotGroup>

                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref flex items-center gap-1.5 pl-4 text-xs tracking-wide text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <EditableToken path="header.phone" as="span">
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                )}
              </div>

              {/* Right: Utility CTA links — large desktop only (xl+).
                  Hidden on tablet / smaller desktop to keep the top bar uncluttered;
                  same links remain available in the drawer menu. */}
              <div className="hidden xl:flex items-center divide-x divide-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_20%,transparent)] ml-auto">
                <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                  <a
                    href={applyNowLink.url || '/apply-now'}
                    target={applyNowLink._target || '_self'}
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    {applyNowLink.label || 'Apply Now'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                  <a
                    href={residentAccessLink.url || '/resident-access'}
                    target={residentAccessLink._target || '_self'}
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    {residentAccessLink.label || 'Resident Access'}
                  </a>
                </EditableSlotGroup>

                <EditableSlotGroup groupPath="header.contact_us_link" definition={contactUsLinkDef}>
                  <a
                    href={contactUsLink.url || '/contact-us'}
                    target={contactUsLink._target || '_self'}
                    className="px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-colors"
                  >
                    {contactUsLink.label || 'Contact Us'}
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Mobile utility row: phone + directions */}
              <div className="md:hidden flex items-center justify-between w-full text-xs">
                {phone && (
                  <a
                    href={`tel:${phone.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref flex items-center gap-1.5 text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-opacity"
                  >
                    <PhoneIcon className="w-3.5 h-3.5" />
                    <span>{formatPhone(phone)}</span>
                  </a>
                )}
                <a
                  href={directionsUrl}
                  target={directionsTarget}
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-[var(--vibe-color-text-on-secondary)] hover:opacity-80 transition-opacity"
                >
                  <MapPinIcon className="w-3.5 h-3.5" />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Main Navigation Bar (transparent)                                */}
        {/* ================================================================ */}
        <div className={`main-nav-bar transition-all duration-500 ease-out ${scrolled ? 'main-nav-bar--scrolled' : ''} ${menuOpen ? 'main-nav-bar--menu-open' : ''}`}>
          <div className="px-4 sm:px-5 lg:px-6 xl:px-8">
            <div ref={navBarRowRef} className="relative flex items-center justify-between gap-3 h-16 lg:h-[72px] max-w-[1600px] mx-auto w-full">
              {/* Logo — capped so a wide mark can't crowd out primary links on large screens */}
              <div ref={logoAreaRef} className="relative z-10 flex-shrink-0 min-w-0 max-w-[140px] sm:max-w-[180px] lg:max-w-[200px] xl:max-w-[220px]">
                <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                  <a href="/" className="flex-shrink-0 block max-w-full">
                    {useDualLogos ? (
                      // Two overlapping logos that crossfade on the same 500ms timing
                      // as the header background transition.
                      // "active bg" = scrolled or menu open (header has solid primary color).
                      <div className="relative h-10 lg:h-12 flex items-center max-w-full">
                        {/* Transparent-state logo (mono_white) — fades out when bg goes solid */}
                        <VibesitesImage
                          url={transparentLogoUrl!}
                          alt={logoAlt}
                          sizes="220px"
                          className={`absolute h-10 lg:h-12 w-auto max-w-full object-contain object-left transition-opacity duration-500 ease-out ${
                            scrolled || menuOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
                          }`}
                        />
                        {/* Scrolled-state logo (luminance-computed) — fades in when bg goes solid */}
                        <VibesitesImage
                          url={scrolledComputedLogoUrl!}
                          alt={logoAlt}
                          sizes="220px"
                          className={`h-10 lg:h-12 w-auto max-w-full object-contain object-left transition-opacity duration-500 ease-out ${
                            scrolled || menuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                          }`}
                        />
                      </div>
                    ) : (transparentLogoUrl || scrolledComputedLogoUrl) ? (
                      <VibesitesImage
                        url={(transparentLogoUrl || scrolledComputedLogoUrl)!}
                        alt={logoAlt}
                        sizes="220px"
                        className="h-10 lg:h-12 w-auto max-w-full object-contain object-left"
                      />
                    ) : (
                      <div className={`font-heading text-xl lg:text-2xl font-bold transition-colors duration-500 ${
                        scrolled || menuOpen ? 'header-logo-text-active' : 'text-white'
                      }`}>
                        The Preferred
                      </div>
                    )}
                  </a>
                </EditableSlotGroup>
              </div>

              {/* Horizontal primary nav — expanded until the row no longer fits. */}
              <div
                className={`desktop-nav-cluster hidden md:flex flex-1 items-center justify-end min-w-0 ${NAV_CLUSTER_CLASS[navDensity]}`}
                style={navLayout === 'drawer' ? { display: 'none' } : undefined}
              >
                <nav
                  className="flex flex-nowrap items-center justify-end min-w-0"
                  aria-label="Primary navigation"
                >
                  {navItems.map((item) => (
                    <DesktopNavLink key={item.href} item={item} density={navDensity} />
                  ))}
                </nav>

                <div className="flex-shrink-0">
                  <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                    <a
                      href={scheduleTourHref}
                      target={scheduleTourLink._target || '_self'}
                      className={`nav-cta-btn group/cta relative inline-flex items-center justify-center font-bold uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300 ${NAV_CTA_CLASS[navDensity]}`}
                    >
                      <span className="relative z-10">{scheduleTourLink.label || 'Schedule a Tour'}</span>
                      <svg
                        className={`relative z-10 transition-transform duration-300 group-hover/cta:translate-x-0.5 ${
                          navDensity === 'comfortable' ? 'w-4 h-4' : 'w-3.5 h-3.5'
                        }`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                      </svg>
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>

              {/* Hamburger — phones, and tablet widths where the link row no longer fits. */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="hamburger-toggle relative w-10 h-10 flex items-center justify-center flex-shrink-0 md:hidden"
                style={navLayout === 'drawer' ? { display: 'flex' } : undefined}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`hamburger-line absolute block w-6 h-0.5 transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* ================================================================ */}
        {/* Drawer menu — mounted only while open so a closed hamburger mode
            never paints a full-viewport layer over the editor preview.      */}
        {/* ================================================================ */}
        {menuOpen && (
        <div
          className={`absolute inset-x-0 top-full mobile-menu-bg overflow-y-auto z-[61] ${navLayout === 'drawer' ? '' : 'md:hidden'}`}
          style={{ maxHeight: `calc(100dvh - ${headerHeight}px - var(--offer-banner-height, 0px))` }}
        >
          <div className="flex flex-col md:flex-row md:items-start">
            {/* Left column: navigation + CTAs */}
            <div className="flex-1 min-w-0 md:border-r md:border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)]">
              <nav role="navigation" aria-label="Primary navigation">
                {navItems.map((item) => (
                  <MobileNavLink
                    key={item.href}
                    item={item}
                    onNavigate={closeMobileMenu}
                  />
                ))}
              </nav>

              <div className="p-5">
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourHref}
                    target={scheduleTourLink._target || '_self'}
                    onClick={closeMobileMenu}
                    className="nav-cta-btn group/cta relative flex items-center justify-center gap-2 w-full py-3.5 text-sm font-bold tracking-[0.15em] uppercase text-[var(--vibe-color-text-on-primary)] overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">{scheduleTourLink.label || 'Schedule a Tour'}</span>
                    <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover/cta:translate-x-0.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                    </svg>
                  </a>
                </EditableSlotGroup>
              </div>

              <div className="px-5 pb-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      onClick={closeMobileMenu}
                      className="text-center block py-2.5 text-[11px] font-bold tracking-wider uppercase border border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_30%,transparent)] text-[var(--vibe-color-text-on-secondary)] hover:bg-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)] hover:border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_60%,transparent)] transition-all rounded-sm"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      onClick={closeMobileMenu}
                      className="text-center block py-2.5 text-[11px] font-bold tracking-wider uppercase border border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_30%,transparent)] text-[var(--vibe-color-text-on-secondary)] hover:bg-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)] hover:border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_60%,transparent)] transition-all rounded-sm"
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>
                  <EditableSlotGroup groupPath="header.contact_us_link" definition={contactUsLinkDef}>
                    <a
                      href={contactUsLink.url || '/contact-us'}
                      target={contactUsLink._target || '_self'}
                      onClick={closeMobileMenu}
                      className="text-center block py-2.5 text-[11px] font-bold tracking-wider uppercase border border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_30%,transparent)] text-[var(--vibe-color-text-on-secondary)] hover:bg-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)] hover:border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_60%,transparent)] transition-all rounded-sm"
                    >
                      {contactUsLink.label || 'Contact Us'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>

            {/* Right column: contact info block, aligned right */}
            <aside
              className="md:w-80 lg:w-96 md:flex-shrink-0 px-5 py-6 border-t md:border-t-0 border-[color-mix(in_srgb,var(--vibe-color-text-on-secondary)_10%,transparent)] text-right"
              aria-label="Contact information"
            >
              {fullAddress && (
                <div className="mb-5">
                  <EditableToken path="header.visit_us_label" as="h2" className="menu-contact-label">
                    {visitUsLabel}
                  </EditableToken>
                  <EditableSlotGroup groupPath="header.visit_us" definition={visitUsLinkDef}>
                    <a
                      href={directionsUrl}
                      target={directionsTarget}
                      rel="noopener noreferrer"
                      className="menu-contact-value hover:text-[var(--vibe-color-text-on-secondary)] transition-colors"
                    >
                      {fullAddress}
                    </a>
                  </EditableSlotGroup>
                </div>
              )}

              {phone && (
                <div className="mb-5">
                  <EditableToken path="header.call_us_label" as="h2" className="menu-contact-label">
                    {callUsLabel}
                  </EditableToken>
                  <a
                    href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                    type="call"
                    className="dniphone dniphonehref menu-contact-value hover:text-[var(--vibe-color-text-on-secondary)] transition-colors"
                  >
                    <EditableToken path="header.phone" as="span">
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>
              )}

              {officeHours.length > 0 && (
                <div>
                  <EditableToken path="header.office_hours_label" as="h2" className="menu-contact-label">
                    {officeHoursLabel}
                  </EditableToken>
                  <dl className="menu-hours-grid">
                    {officeHours.map((h, i) => (
                      <React.Fragment key={i}>
                        <dt className="menu-hours-day">{formatDay(h.day)}</dt>
                        <dd className="menu-hours-time">{formatHoursValue(h.open_time, h.close_time)}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </div>
              )}
            </aside>
          </div>
        </div>
        )}
      </header>

      <style>{`
        /* ============================================================ */
        /* Utility bar                                                   */
        /* ============================================================ */
        .header-utility-bar {
          background: linear-gradient(
            135deg,
            var(--vibe-color-secondary) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 85%, black) 100%
          );
          color: var(--vibe-color-text-on-secondary);
        }

        /* ============================================================ */
        /* Main nav bar — transparent by default, solid on scroll        */
        /* ============================================================ */
        .main-nav-bar {
          background: transparent;
        }
        .main-nav-bar--scrolled {
          background: var(--vibe-color-primary);
          border-bottom-color: transparent;
        }

        /* When mobile menu is open, give the nav bar a solid background
           so the hero image doesn't bleed through behind the logo/close row */
        .main-nav-bar--menu-open {
          background: var(--vibe-color-primary);
          border-bottom-color: transparent;
        }

        /* ============================================================ */
        /* Fallback text logo when no image is available                 */
        /* ============================================================ */
        .header-logo-text-active {
          color: var(--vibe-color-text-on-primary);
        }

        /* ============================================================ */
        /* Offscreen measures for overflow-aware desktop nav             */
        /* ============================================================ */
        .nav-fit-measure-wrap {
          position: fixed;
          left: -10000px;
          top: 0;
          width: max-content;
          max-width: none;
          visibility: hidden;
          pointer-events: none;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }
        .nav-fit-measure {
          display: flex;
          width: max-content;
          flex-shrink: 0;
          align-items: center;
          gap: 0.125rem;
          white-space: nowrap;
        }

        /* When the compact link row still collides with the logo, swap it
           for the hamburger above the md breakpoint (tablet). */
        @media (min-width: 768px) {
          .nav-layout-drawer .desktop-nav-cluster {
            display: none;
          }
          .nav-layout-drawer .hamburger-toggle {
            display: flex;
          }
        }

        /* ============================================================ */
        /* Desktop primary nav links                                     */
        /* ============================================================ */
        .desktop-nav-link {
          color: white;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
        }
        .desktop-nav-link:hover {
          color: white;
        }
        .main-nav-bar--scrolled .desktop-nav-link,
        .main-nav-bar--menu-open .desktop-nav-link {
          color: color-mix(in srgb, var(--vibe-color-text-on-primary) 85%, transparent);
          text-shadow: none;
        }
        .main-nav-bar--scrolled .desktop-nav-link:hover,
        .main-nav-bar--menu-open .desktop-nav-link:hover {
          color: var(--vibe-color-text-on-primary);
        }

        /* ============================================================ */
        /* Hamburger lines — white on transparent, textOnPrimary on solid */
        /* ============================================================ */
        .hamburger-line {
          background-color: white;
          box-shadow: 0 0 1px rgba(0, 0, 0, 0.85);
          transition: background-color 0.5s ease;
        }
        .main-nav-bar--scrolled .hamburger-line,
        .main-nav-bar--menu-open .hamburger-line {
          background-color: var(--vibe-color-text-on-primary);
        }

        /* ============================================================ */
        /* Hamburger menu background                                     */
        /* ============================================================ */
        .mobile-menu-bg {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--vibe-color-secondary) 95%, black) 0%,
            color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%
          );
        }

        /* ============================================================ */
        /* Hamburger menu contact info block                             */
        /* ============================================================ */
        .menu-contact-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 50%, transparent);
          margin: 0 0 0.4rem;
        }
        .menu-contact-value {
          display: block;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 80%, transparent);
          text-decoration: none;
        }
        .menu-hours-grid {
          display: grid;
          grid-template-columns: auto auto;
          justify-content: end;
          column-gap: 1rem;
          row-gap: 0.2rem;
          margin: 0;
        }
        .menu-hours-day {
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          font-weight: 600;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 90%, transparent);
          text-align: left;
        }
        .menu-hours-time {
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          color: color-mix(in srgb, var(--vibe-color-text-on-secondary) 60%, transparent);
          text-align: right;
          margin: 0;
        }

        /* ============================================================ */
        /* Schedule tour CTA button                                      */
        /* ============================================================ */
        .nav-cta-btn {
          background: linear-gradient(
            135deg,
            var(--vibe-color-primary) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%
          );
          border-radius: 9999px;
          box-shadow:
            0 1px 3px rgba(0,0,0,0.12),
            0 2px 8px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
          transition:
            background 0.5s ease,
            box-shadow 0.3s ease,
            color 0.5s ease,
            transform 0.3s ease;
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(255,255,255,0.15) 50%,
            transparent 100%
          );
          transition: left 0.5s ease;
        }
        .nav-cta-btn:hover {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%,
            var(--vibe-color-primary) 100%
          );
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 35%, transparent);
          transform: translateY(-1px);
        }
        .nav-cta-btn:hover::before {
          left: 100%;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 3px rgba(0,0,0,0.12);
        }

        /* Scrolled / solid primary bar — use text-on-primary (the color that
           sits on the scrolled header) so the pill matches that chrome and
           stays readable against the primary bar. */
        .main-nav-bar--scrolled .nav-cta-btn,
        .main-nav-bar--menu-open .nav-cta-btn {
          background: var(--vibe-color-text-on-primary);
          color: var(--vibe-color-primary);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
        }
        .main-nav-bar--scrolled .nav-cta-btn:hover,
        .main-nav-bar--menu-open .nav-cta-btn:hover {
          background: color-mix(in srgb, var(--vibe-color-text-on-primary) 92%, var(--vibe-color-primary));
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
      // Empty so a missing call-tracking number falls through to property_phone
      // in the component (placeholder is applied there as the final fallback).
      defaultValue: '',
    },
    'header.visit_us_label': {
      label: 'Visit Us Heading',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'header.call_us_label': {
      label: 'Call Us Heading',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'header.office_hours_label': {
      label: 'Office Hours Heading',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        // No defaultField — mono_white vs mono_black is computed at runtime via luminance
        image: { label: 'Logo Image', type: 'image', suggestedFields: ['property_mono_white_logo', 'property_mono_black_logo', 'property_primary_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'richtext', defaultValue: '<p>Community Logo</p>' },
      },
    },
    'header.visit_us': {
      label: 'Visit Us',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at render
        // time. Editor overrides still win.
        url: {
          label: 'Directions URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultField: 'property_resident_portal_url', suggestedFields: ['property_resident_portal_url'] },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.contact_us_link': {
      label: 'Contact Us Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/contact-us' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Contact Us' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        // No defaultField / defaultValue — href is computed at render from the
        // AnyoneHome scheduler URL (→ /schedule-a-tour) or Contact Us fallback.
        // Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
    'header.virtual_tours': {
      label: 'Virtual Tours (visibility check)',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
